import React, { useContext, useMemo } from 'react';
import {
  CustomField,
  CustomFieldEntityType,
  CustomFieldType,
  CustomFieldValue,
  WithCustomFields,
} from '../../../types/custom_field/customField';
import { CustomFieldContext } from '../../../context/CustomFieldContext';
import { Grid } from '@mui/material';
import CustomFieldInput from './CustomFieldInput';
import { classes, testIds } from '../../../util/identifiers/identifiers.util';
import { OrderInput, OrderUtil } from '../../../types/order/';
import { UserContext } from '../../../context/UserContext';
import { ContactContext } from '../../../context/ContactContext';
import { ProductMasterDataContext } from '../../../context/ProductMasterDataContext';
import { Divider } from '../../components/Divider';
import { cloneDeep } from '@apollo/client/utilities';

interface CustomFieldOrderInputProps {
  order: OrderInput;
  setOrder: (order: OrderInput) => void;
  disabled?: boolean;
}

export default function CustomFieldOrderInput({ order, setOrder, disabled = false }: CustomFieldOrderInputProps) {
  const { customFields } = useContext(CustomFieldContext);
  const { companyUsers } = useContext(UserContext);
  const { contacts } = useContext(ContactContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const entitySubtype = OrderUtil.typeToEntitySubType(order.type);

  const items = useMemo(() => {
    return [...customFields.values()].filter(
      v =>
        !v.hidden &&
        (!v.generated || order.id) &&
        v.entityType === CustomFieldEntityType.order &&
        (!v.entitySubtype || v.entitySubtype === entitySubtype) &&
        (v.onCreate || order.id),
    );
  }, [customFields, order]);

  const customFieldText = (field: CustomField, value?: string) => {
    if (!value) return '';

    if (field.type === CustomFieldType.date) {
      return value;
    }

    return value || '';
  };

  const content = (item: CustomField, cf?: CustomFieldValue) => {
    if (item.generated && cf?.value === undefined) return null;

    return (
      <Grid
        key={`${cf?.id || item.id}${cf?.name || item.name}`}
        item
        xs={item.manual ? 3 : [CustomFieldType.bool, CustomFieldType.image].includes(item.type) ? 12 : 6}
        mt={item.type === CustomFieldType.bool ? 1 : 0}
        className={classes.customFieldInput.name}
        data-testid={testIds.customFieldInput(item.name)}
      >
        <CustomFieldInput
          disabled={(disabled && item.onCreate) || !!(order.id && item.mandatory) || (item.generated && item.onCreate)}
          value={customFieldText(item, cf?.value)}
          entity={order}
          item={item}
          onChange={setOrder}
          name={cf?.name}
          generated={cf?.generated}
          index={cf?.index}
          id={cf?.id}
          users={companyUsers}
          contacts={contacts}
          productMasterData={productMasterData}
        />
      </Grid>
    );
  };

  const customValues = order.customFieldValues();

  const children = useMemo(() => {
    const displayed: Set<string> = new Set();

    const customFieldValueItems: CustomFieldValue[] = [];
    for (const item of items) {
      const cf = WithCustomFields.toCustomFieldsMap(order).get(item.id);
      displayed.add(item.id);
      customFieldValueItems.push(cf || CustomField.toValue(item, ''));
    }

    for (const v of customValues) {
      if (displayed.has(v.id)) continue;

      const cfValue = cloneDeep(v);

      const [id, suffix] = cfValue.id.split('::');

      const cf = customFields.get(id);
      if (!cf) continue;

      if (suffix) {
        const count = customValues.filter(cv => cv.name.includes(cf.name)).length - 1;
        if (count) cfValue.name = `${cf.name} (${count})`;
        cfValue.id = cf.id;
        cfValue.index = cf.index;
      }

      customFieldValueItems.push(cfValue);
    }

    return customFieldValueItems
      .sort((a, b) => b.index - a.index || a.name.localeCompare(b.name))
      .map(item => {
        const cf = customFields.get(item.id);
        if (!cf) return null;

        return content(cf, item);
      });
  }, [items, customValues, customFields, order]);

  if (!children.length) return null;

  return (
    <>
      <Divider className='my-4' />
      <Grid container rowSpacing={1} columnSpacing={1}>
        {children}
      </Grid>
    </>
  );
}
